import React from 'react';
import { alpha, Box, ButtonBase, Divider, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PanToolAltOutlinedIcon from '@mui/icons-material/PanToolAltOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';

import { SegmentSelect, TagSelect, TagsSelect } from '../outreach/Pickers';
import { InspectorShell } from './StepInspector';
import { formatHour, LIMITS, settingsProblems, TriggerType, WorkflowSettings } from './workflowModel';

/**
 * Who comes in, and the guards on what they receive: the trigger card's panel.
 * Editable on a draft or a paused workflow (the API's rule); a changed trigger
 * reads from when the workflow next starts.
 */

const TRIGGERS: Array<{ type: TriggerType; label: string; hint: string; icon: React.ReactNode }> = [
  { type: 'MANUAL', label: 'By hand', hint: 'Enrol people from a list, a tag or the contact list', icon: <PanToolAltOutlinedIcon /> },
  { type: 'TAG_ADDED', label: 'A tag is added', hint: 'People join as they are given a tag', icon: <LocalOfferOutlinedIcon /> },
  { type: 'SEGMENT', label: 'Joins a list', hint: 'Or starts matching a saved filter', icon: <PlaylistAddOutlinedIcon /> },
];

interface TriggerInspectorProps {
  settings: WorkflowSettings;
  editable: boolean;
  /** Why it cannot be changed, when it cannot. */
  lockedReason?: string;
  showErrors: boolean;
  onChange: (settings: WorkflowSettings) => void;
  onClose: () => void;
}

const numberOrNull = (text: string) => (text.trim() === '' ? null : Math.trunc(Number(text)));

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'grid', gap: 1.5 }}>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export const TriggerInspector = ({ settings, editable, lockedReason, showErrors, onChange, onClose }: TriggerInspectorProps) => {
  const set = (patch: Partial<WorkflowSettings>) => onChange({ ...settings, ...patch });
  const problems = showErrors ? settingsProblems(settings) : {};
  const quiet = settings.quietHoursStart != null && settings.quietHoursEnd != null;

  return (
    <InspectorShell tone="#4f46e5" icon={<BoltIcon fontSize="small" />} overline="Trigger and guards" title="Who comes in" onClose={onClose}>
      {lockedReason && (
        <Typography variant="body2" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff7ed', color: '#9a3412' }}>
          {lockedReason}
        </Typography>
      )}

      <Section title="Trigger">
        <Box role="radiogroup" aria-label="Trigger" sx={{ display: 'grid', gap: 0.75 }}>
          {TRIGGERS.map((t) => {
            const on = settings.triggerType === t.type;
            return (
              <ButtonBase
                key={t.type}
                role="radio"
                aria-checked={on}
                disabled={!editable}
                onClick={() => set({ triggerType: t.type })}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  gap: 1.25,
                  p: 1.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: on ? 'primary.main' : 'divider',
                  bgcolor: on ? alpha('#4f46e5', 0.05) : 'transparent',
                  opacity: !editable && !on ? 0.5 : 1,
                  transition: 'all 120ms',
                  '& svg': { color: on ? 'primary.main' : 'text.secondary', fontSize: 20 },
                }}
              >
                {t.icon}
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.hint}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>

        {settings.triggerType === 'TAG_ADDED' && (
          <TagSelect
            label="The tag"
            value={settings.triggerTagId}
            valueName={settings.triggerTagName}
            onChange={(tag) => set({ triggerTagId: tag?.id ?? null, triggerTagName: tag?.name ?? null })}
            error={problems.triggerTagId}
            disabled={!editable}
          />
        )}
        {settings.triggerType === 'SEGMENT' && (
          <SegmentSelect
            label="The list or saved filter"
            value={settings.triggerSegmentId ? { id: settings.triggerSegmentId, name: settings.triggerSegmentName ?? `#${settings.triggerSegmentId}` } : null}
            onChange={(segment) => set({ triggerSegmentId: segment?.id ?? null, triggerSegmentName: segment?.name ?? null })}
            error={problems.triggerSegmentId}
            helperText="A saved filter is checked every 15 minutes for people who start matching it."
            disabled={!editable}
          />
        )}
        {settings.triggerType !== 'MANUAL' && (
          <FormControlLabel
            disabled={!editable}
            control={<Switch checked={settings.includeExisting} onChange={(event) => set({ includeExisting: event.target.checked })} />}
            label={
              <Typography variant="body2">
                Also enrol everyone {settings.triggerType === 'TAG_ADDED' ? 'who has the tag' : 'on it'} already, when it first starts
              </Typography>
            }
          />
        )}
      </Section>

      <Divider />

      <Section title="Guards">
        <FormControlLabel
          disabled={!editable}
          control={<Switch checked={settings.allowReentry} onChange={(event) => set({ allowReentry: event.target.checked, reentryCooldownH: event.target.checked ? settings.reentryCooldownH ?? 24 : null })} />}
          label={<Typography variant="body2">Let people come through it again</Typography>}
        />
        {settings.allowReentry ? (
          <TextField
            label="After (hours since they last came in)"
            type="number"
            value={settings.reentryCooldownH ?? ''}
            disabled={!editable}
            onChange={(event) => set({ reentryCooldownH: numberOrNull(event.target.value) })}
            error={!!problems.reentryCooldownH}
            helperText={problems.reentryCooldownH}
            inputProps={{ min: 0, max: LIMITS.reentryCooldownH }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Off: anyone who has ever been in it is never enrolled again.
          </Typography>
        )}

        <FormControlLabel
          disabled={!editable}
          control={
            <Switch
              checked={quiet}
              onChange={(event) => set(event.target.checked ? { quietHoursStart: 21, quietHoursEnd: 8 } : { quietHoursStart: null, quietHoursEnd: null })}
            />
          }
          label={<Typography variant="body2">Quiet hours: messages wait them out</Typography>}
        />
        {quiet && (
          <Stack direction="row" spacing={1}>
            {(['quietHoursStart', 'quietHoursEnd'] as const).map((key) => (
              <TextField
                key={key}
                select
                label={key === 'quietHoursStart' ? 'From' : 'Until'}
                value={settings[key] ?? 0}
                disabled={!editable}
                onChange={(event) => set({ [key]: Number(event.target.value) })}
                error={!!problems[key]}
                helperText={problems[key]}
                sx={{ flex: 1 }}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <MenuItem key={h} value={h}>
                    {formatHour(h)}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1}>
          <TextField
            label="Messages a day, each"
            type="number"
            value={settings.maxPerContactPerDay ?? ''}
            placeholder="No cap"
            disabled={!editable}
            onChange={(event) => set({ maxPerContactPerDay: numberOrNull(event.target.value) })}
            error={!!problems.maxPerContactPerDay}
            helperText={problems.maxPerContactPerDay}
            inputProps={{ min: 1, max: LIMITS.maxPerContactPerDay }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Sends a day, in all"
            type="number"
            value={settings.maxSendsPerDay ?? ''}
            placeholder="No cap"
            disabled={!editable}
            onChange={(event) => set({ maxSendsPerDay: numberOrNull(event.target.value) })}
            error={!!problems.maxSendsPerDay}
            helperText={problems.maxSendsPerDay}
            inputProps={{ min: 1 }}
            sx={{ flex: 1 }}
          />
        </Stack>

        <TagsSelect
          label="Take them out when tagged"
          value={settings.exitTagIds}
          onChange={(ids) => set({ exitTagIds: ids })}
          max={LIMITS.exitTags}
          error={problems.exitTagIds}
          helperText="NationBuilder's “remove when tagged”: anyone carrying one leaves the workflow."
          disabled={!editable}
        />
      </Section>
    </InspectorShell>
  );
};
